import { AUDIENCE_LABEL, CATEGORY_LABELS } from './watch-format.js';
import { NOTIFICATION_EVENTS_BY_ID } from '../../../shared/notifications.mjs';
import { dailyRuleKey, dependencyContext, liveRuleKey, ruleDependency } from '../../../shared/notification-dependencies.mjs';

export const SOURCE = Object.freeze({ daily: 'Daily collection', live: 'Live watch' });
export const DAILY_CADENCE = Object.freeze({ label: 'After each collection', hint: 'Evaluated by the daily collector run, on your schedule.' });
export const LIVE_CADENCE = Object.freeze({ label: 'Immediate or digest', hint: 'Critical events are always immediate. Everything else follows each recipient preference and goes out in the daily digest.' });
export const MY_ACCOUNT_ROW_ID = 'live:my-account';
export const ADMIN_NOTICES_ROW_ID = 'live:admin-notices';

const enabledOf = (toggles, id) => toggles?.[id] !== false;

export function recipientChips(recipients = [], alertEmail = '') {
  return recipients.map((r, i) => {
    switch (r.type) {
      case 'fixed': return { id: `f${i}`, label: r.email };
      case 'fallback': return { id: `b${i}`, label: `Fallback: ${r.email}` };
      case 'people': return { id: `p${i}`, label: (r.emails ?? []).length === 1 ? r.emails[0] : `${(r.emails ?? []).length} people` };
      case 'group-owners': return { id: `g${i}`, label: 'Group owners' };
      case 'default': return { id: `d${i}`, label: alertEmail ? `Default address (${alertEmail})` : 'Default address' };
      default: return { id: `x${i}`, label: String(r.type) };
    }
  });
}

/** The catalog audiences of a live event that are switched on; administrators always are. */
export function liveAudience(entryAudiences = [], flags = {}) {
  const on = { self: flags.self === true, owners: flags.ownersWatched === true || flags.ownersAll === true, guardians: flags.guardians === true, admins: true };
  return entryAudiences.filter((a) => on[a]).map((a) => ({ id: a, label: AUDIENCE_LABEL[a] ?? a }));
}

/** Live rules on, from the GET /watch payload: toggleable events not switched off, plus My account notices. */
export function countLiveRulesOn(watch) {
  const s = watch?.settings;
  if (!s) return 0;
  const events = (watch.catalog?.events ?? []).filter((e) => e.toggleable);
  return events.filter((e) => enabledOf(s.eventToggles, e.id)).length + (s.audiences?.self ? 1 : 0);
}

const AVAILABLE = Object.freeze({ available: true });

/**
 * What each rule needs, checked with the shared map (portal/shared/notification-dependencies.mjs) against the
 * data source state from GET /notifications (`sources`) and Watch's health. Without `sources` (an older portal
 * API) nothing is blocked: the backend still refuses to switch on a rule that cannot work.
 */
export function ruleDependencies({ sources, watchHealth }) {
  if (!sources) return () => AVAILABLE;
  const ctx = dependencyContext({ config: sources, watchHealth });
  return (key) => ruleDependency(key, ctx);
}

export function buildRuleRows({ rules, classicCatalog, watch, alertEmail = '', sources = null }) {
  // A rule whose source is off is missing from the catalog (it lists the events that can be chosen), so its label comes from the full one.
  const label = (eventId) => classicCatalog?.find((e) => e.id === eventId)?.label ?? NOTIFICATION_EVENTS_BY_ID[eventId]?.label ?? eventId;
  const dependencyOf = ruleDependencies({ sources, watchHealth: watch?.health });
  const rows = (rules ?? []).map((r) => ({
    id: `daily:${r.ruleId}`,
    source: 'daily',
    title: label(r.eventId),
    category: null,
    audience: recipientChips(r.recipients, alertEmail),
    cadence: DAILY_CADENCE,
    enabled: r.enabled === true,
    locked: false,
    dependency: dependencyOf(dailyRuleKey(r.eventId)),
    rule: r,
  }));
  if (watch?.settings && watch?.catalog) {
    const s = watch.settings;
    for (const e of watch.catalog.events.filter((x) => x.toggleable)) {
      rows.push({ id: `live:${e.id}`, source: 'live', title: e.label, category: e.category, audience: liveAudience(e.audiences, s.audiences), cadence: LIVE_CADENCE, enabled: enabledOf(s.eventToggles, e.id), locked: false, catalogId: e.id, dependency: dependencyOf(liveRuleKey(e.id)) });
    }
    rows.push({ id: MY_ACCOUNT_ROW_ID, source: 'live', title: 'My account notices', category: 'my-account', audience: [{ id: 'self', label: AUDIENCE_LABEL.self }], cadence: LIVE_CADENCE, enabled: s.audiences?.self === true, locked: false, dependency: AVAILABLE });
    rows.push({
      id: ADMIN_NOTICES_ROW_ID,
      source: 'live',
      title: 'Administrator notices',
      category: 'integrity',
      audience: [{ id: 'admins', label: AUDIENCE_LABEL.admins }, { id: 'guardians', label: AUDIENCE_LABEL.guardians }],
      cadence: { label: 'Immediate', hint: 'Changes to Watch itself are announced before they take effect. These cannot be turned off.' },
      enabled: true,
      locked: true,
      dependency: AVAILABLE,
    });
  }
  return rows;
}

/** The PATCH /watch/settings body for one live row's switch. */
export function liveTogglePatch(row, next) {
  if (row.id === MY_ACCOUNT_ROW_ID) return { audiences: { self: next } };
  return { eventToggles: { [row.catalogId]: next } };
}

export const ruleSearchText = (r) => [r.title, SOURCE[r.source], ...r.audience.map((a) => a.label), r.category ? (CATEGORY_LABELS[r.category] ?? r.category) : null].filter(Boolean).join(' ');
